import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import * as Icons from 'lucide-react';
import { ArrowRight, Zap, ShieldCheck, Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { Stagger, StaggerItem } from '@/components/Reveal';
import { FloatingChip } from '@/components/PhoneMockup';
import { DeviceFrame, SignalsScreen } from '@/components/AppScreens';
import { SpotlightCard } from '@/components/SpotlightCard';
import { StoreBadges } from '@/components/StoreBadges';
import { SectionHeading } from '@/components/sections/SectionHeading';
import { PathChooser } from '@/components/sections/PathChooser';
import { CapabilityStrip } from '@/components/sections/CapabilityStrip';
import { TrustSection } from '@/components/sections/TrustSection';
import { PricingCards } from '@/components/sections/PricingCards';
import { FaqAccordion } from '@/components/sections/FaqAccordion';
import { CtaBanner } from '@/components/sections/CtaBanner';

export default function Home() {
  const { t } = useTranslation();
  const heroRef = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const phoneScale = useTransform(scrollYProgress, [0, 1], [1, 0.92]);
  const phoneRotate = useTransform(scrollYProgress, [0, 1], [0, -4]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 180]);

  const steps = t('home.steps', { returnObjects: true });
  const faqs = t('faqs', { returnObjects: true });
  const trustChips = t('home.trustChips', { returnObjects: true });
  const chipIcons = [Check, Copy, ShieldCheck];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'SoftwareApplication', name: 'Copy.Click', applicationCategory: 'FinanceApplication', operatingSystem: 'iOS, Android', offers: { '@type': 'Offer', price: '9.00', priceCurrency: 'USD' } },
      { '@type': 'Organization', name: 'Copy.Click', url: 'https://copy.click', logo: 'https://copy.click/logo-mark.svg' },
      { '@type': 'FAQPage', mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
    ],
  };

  return (
    <>
      <Seo description={t('home.seoDescription')} jsonLd={jsonLd} />

      {/* HERO */}
      <section ref={heroRef} className="bg-deep relative overflow-hidden">
        <motion.div style={{ y: glowY }} className="animate-blob pointer-events-none absolute right-0 top-10 -z-0 h-[520px] w-[520px] rounded-full bg-brand/20 blur-[140px]" />
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 pb-20 pt-32 sm:pt-40 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:pb-28">
          <Stagger className="relative z-10 text-center lg:text-left" gap={0.1}>
            <StaggerItem>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand" /> {t('home.badge')}
              </span>
            </StaggerItem>
            <StaggerItem>
              <h1 className="mt-6 break-words font-display text-4xl font-semibold leading-[1.06] tracking-tight text-white sm:text-6xl lg:text-7xl">
                {t('home.titleLine1')}<br /><span className="text-shimmer">{t('home.titleLine2')}</span>
              </h1>
            </StaggerItem>
            <StaggerItem>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-skyaccent/80 lg:mx-0">
                {t('home.subtitle')}
              </p>
            </StaggerItem>
            <StaggerItem>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4 lg:justify-start">
                <Link to="/pricing" data-testid="hero-cta-trial" className="btn-primary cta-pulse text-base">
                  {t('common.start7DayTrial')} <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="#how-it-works" data-testid="hero-cta-how" className="btn-secondary text-base">{t('home.ctaHow')}</a>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 lg:justify-start">
                {trustChips.map((label, i) => {
                  const Icon = chipIcons[i] || Check;
                  return (
                    <span key={label} className="flex items-center gap-1.5 text-xs font-medium text-white/55">
                      <Icon className="h-3.5 w-3.5 text-brand" /> {label}
                    </span>
                  );
                })}
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="mt-8 flex justify-center lg:justify-start"><StoreBadges /></div>
            </StaggerItem>
          </Stagger>

          <div className="perspective relative flex justify-center lg:justify-end">
            <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} style={{ y: phoneY, scale: phoneScale, rotate: phoneRotate }} className="relative">
              <DeviceFrame tilt width="w-[280px] sm:w-[300px]"><SignalsScreen /></DeviceFrame>
              <FloatingChip delay={0.6} className="absolute -left-4 top-10 z-20 hidden sm:block sm:-left-12">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand/15 text-brand"><Copy className="h-4 w-4" /></span>
                <div className="leading-tight"><p className="text-[11px] text-white/50">{t('home.chipTradeCopied')}</p><p className="text-sm font-semibold text-white">EURUSD · SELL</p></div>
              </FloatingChip>
              <FloatingChip delay={1.1} className="absolute -right-6 top-1/3 z-20 hidden sm:block">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand/15 text-brand"><Zap className="h-4 w-4" /></span>
                <div className="leading-tight"><p className="text-[11px] text-white/50">{t('home.chipExecution')}</p><p className="text-sm font-semibold text-white">{t('home.chipRealtime')}</p></div>
              </FloatingChip>
              <FloatingChip delay={1.5} className="absolute -bottom-5 left-0 z-20 hidden sm:block sm:left-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand/15 text-brand"><ShieldCheck className="h-4 w-4" /></span>
                <div className="leading-tight"><p className="text-[11px] text-white/50">{t('home.chipRiskMultiplier')}</p><p className="text-sm font-semibold text-white">1.0x</p></div>
              </FloatingChip>
              <motion.div
                className="absolute bottom-20 -right-6 z-30 hidden sm:block"
                animate={{ opacity: [0, 1, 1, 0], y: [12, 0, 0, -12], scale: [0.9, 1, 1, 0.95] }}
                transition={{ duration: 4.5, times: [0, 0.14, 0.8, 1], repeat: Infinity, repeatDelay: 1.6, ease: 'easeInOut' }}
              >
                <div className="flex items-center gap-2.5 rounded-2xl border border-[#00FFA3]/30 bg-[#001826]/90 px-4 py-2.5 shadow-[0_12px_40px_rgba(0,0,0,0.55)] backdrop-blur-xl">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#00FFA3]/15 text-[#00FFA3]"><Check className="h-3.5 w-3.5" /></span>
                  <div className="leading-tight"><p className="text-[11px] text-white/50">{t('home.chipTradeExecuted')}</p><p className="text-sm font-semibold text-[#00FFA3]">XAU/USD · BUY</p></div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <CapabilityStrip />
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="scroll-mt-20 px-6 py-24 sm:py-32 lg:px-8">
        <SectionHeading eyebrow={t('home.howEyebrow')} title={t('home.howTitle')} subtitle={t('home.howSubtitle')} />
        <div className="relative mx-auto mt-16 max-w-6xl">
          <div className="pointer-events-none absolute left-0 right-0 top-[3.75rem] hidden h-px bg-gradient-to-r from-transparent via-brand/25 to-transparent md:block" />
          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((step, i) => {
              const Icon = Icons[step.icon] || Icons.Check;
              return (
                <motion.div key={step.title} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}>
                  <SpotlightCard className="group relative flex h-full flex-col rounded-3xl border border-white/10 bg-[#001826]/70 p-8 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-brand/30 hover:shadow-[0_24px_60px_rgba(23,253,253,0.10)]">
                    <span className="absolute right-7 top-6 font-display text-6xl font-bold text-white/[0.04] transition-colors group-hover:text-brand/10">0{i + 1}</span>
                    <span className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#001826] ring-1 ring-brand/20 text-brand shadow-[0_0_28px_rgba(23,253,253,0.25)] transition-transform duration-300 group-hover:scale-110"><Icon className="h-7 w-7" /></span>
                    <h3 className="mt-6 font-display text-xl font-semibold text-white">{step.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-skyaccent/70">{step.desc}</p>
                  </SpotlightCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <PathChooser />

      <TrustSection />

      {/* PRICING */}
      <section className="px-6 py-24 sm:py-32 lg:px-8">
        <SectionHeading eyebrow={t('home.pricingEyebrow')} title={t('home.pricingTitle')} subtitle={t('home.pricingSubtitle')} />
        <div className="mx-auto mt-14 max-w-6xl"><PricingCards /></div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-center lg:sticky lg:top-28 lg:text-left">
              <span className="eyebrow">{t('home.faqEyebrow')}</span>
              <h2 className="mt-4 font-display text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-[2.75rem]">
                {t('home.faqTitle')}
              </h2>
              <p className="mx-auto mt-5 max-w-md text-base leading-relaxed text-white/60 lg:mx-0">
                {t('home.faqSubtitle')} <Link to="/faq" className="text-brand hover:underline">{t('home.faqLink')}</Link>.
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <FaqAccordion />
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
